import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";

// Client functions
import { updateReadTranscript } from "sj-ui-toolkit/src/api";
import { getLessonTranscriptUrl } from "sj-ui-toolkit/src/client";
import { resolveImageUrl } from "sj-ui-toolkit/src/utils/url";

import S from "./TextViewer.module.scss";

const CODE_STYLES_CSS = `pre code { color: #1E2126; }`;

const ZOOMABLE_IMAGE_CSS = `
img[data-zoomable] { cursor: zoom-in; }
img[data-zoomable]:focus-visible { outline: 2px solid #005fcc; outline-offset: 2px; }
`;

interface ZoomedImage {
  src: string;
  alt: string;
}

// Snyk does not recognise resolveImageUrl as a sanitizer, so the <img src> sink needs this inline guard.
const IMAGE_URL_PATTERN = /^(https?:\/\/|data:image\/)/i;

// Listeners sit on the shadow root, so event.target is not retargeted to the host.
const getTargetImage = (event: Event): HTMLImageElement | null => {
  const { target } = event;
  return target instanceof HTMLImageElement && target.hasAttribute("data-zoomable") ? target : null;
};

const TranscriptShadow = ({ bodyHtml, styleCss }: { bodyHtml: string; styleCss: string }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const triggerIndexRef = useRef(-1);
  const [zoomed, setZoomed] = useState<ZoomedImage | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadow.innerHTML = "";

    // CSS injected as text content — no sanitization needed
    const styleEl = document.createElement("style");
    styleEl.textContent = CODE_STYLES_CSS + styleCss + ZOOMABLE_IMAGE_CSS;
    shadow.appendChild(styleEl);

    // HTML body sanitized with DOMPurify’s default config
    const body = document.createElement("div");
    body.innerHTML = DOMPurify.sanitize(bodyHtml);
    shadow.appendChild(body);

    // Images inside links keep their link behavior
    body.querySelectorAll("img").forEach(img => {
      if (!resolveImageUrl(img.getAttribute("src")) || img.closest("a")) return;
      img.setAttribute("data-zoomable", "");
      img.setAttribute("tabindex", "0");
      img.setAttribute("role", "button");
      img.setAttribute("aria-haspopup", "dialog");
      img.setAttribute("aria-label", img.alt ? `View larger: ${img.alt}` : "View larger image");
    });

    const open = (img: HTMLImageElement) => {
      const src = resolveImageUrl(img.currentSrc || img.src);
      if (!src) return;
      const zoomable = Array.from(body.querySelectorAll("img[data-zoomable]"));
      triggerIndexRef.current = zoomable.indexOf(img);
      setZoomed({ src, alt: img.alt });
    };

    const handleClick = (event: Event) => {
      const img = getTargetImage(event);
      if (img) open(img);
    };

    const handleKeyDown = (event: Event) => {
      if (!(event instanceof KeyboardEvent)) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      const img = getTargetImage(event);
      if (!img) return;
      event.preventDefault();
      open(img);
    };

    shadow.addEventListener("click", handleClick);
    shadow.addEventListener("keydown", handleKeyDown);
    return () => {
      shadow.removeEventListener("click", handleClick);
      shadow.removeEventListener("keydown", handleKeyDown);
    };
  }, [bodyHtml, styleCss]);

  const handleHide = () => {
    setZoomed(null);
    // Re-query by index: the shadow DOM may have been rebuilt while the dialog was open.
    const images = hostRef.current?.shadowRoot?.querySelectorAll<HTMLElement>("img[data-zoomable]");
    images?.[triggerIndexRef.current]?.focus();
  };

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        ref={hostRef}
        style={{
          containerType: "inline-size",
          position: "absolute",
          inset: 0,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          overflowX: "hidden",
          wordBreak: "break-word",
        }}
      />
      <Dialog
        visible={zoomed !== null}
        onHide={handleHide}
        header={zoomed?.alt || "Image"}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        className={S.imageDialog}
      >
        {zoomed && IMAGE_URL_PATTERN.test(zoomed.src) && (
          <img className={S.zoomedImage} src={zoomed.src} alt={zoomed.alt} />
        )}
      </Dialog>
    </div>
  );
};

interface TextViewerProps {
  lessonId: number;
  readTranscript: boolean;
}

const TextViewer = ({ lessonId, readTranscript }: TextViewerProps) => {
  const [transcriptBody, setTranscriptBody] = useState<string | null>(null);
  const [transcriptCss, setTranscriptCss] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTranscript = async () => {
      setLoading(true);
      try {
        const url = await getLessonTranscriptUrl({ lessonId: String(lessonId) });
        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const styleCss = Array.from(doc.querySelectorAll("style"))
          .map(el =>
            (el.textContent ?? "").replace(
              /@media\s*(\([^)]*(?:max-width|min-width)[^)]*\))/g,
              "@container $1"
            )
          )
          .join("\n");

        setTranscriptCss(styleCss);
        setTranscriptBody(doc.body.innerHTML);
      } catch (error) {
        console.error("Error fetching transcript:", error);
      }
      setLoading(false);
    };

    if (lessonId && transcriptBody === null) {
      fetchTranscript();
    }
  }, [lessonId]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!readTranscript && event.data === "reachedEnd") {
        // updateReadTranscript types levelId as required + lessonId as string; callers only have
        // the numeric lessonId and the backend tolerates the missing levelId. TODO(T12): loosen.
        const result = await updateReadTranscript({ lessonId: String(lessonId) } as {
          levelId: string;
          lessonId: string;
        });
        if (!result) {
          console.error("Failed to send read transcript event");
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [lessonId]);

  if (loading) return <div>Loading transcript...</div>;

  return <TranscriptShadow bodyHtml={transcriptBody ?? ""} styleCss={transcriptCss} />;
};

export default TextViewer;
